/**
 * PassiveOperatorWarning — the box a persistent operator notice is drawn in,
 * and nothing else (#493).
 *
 * It knows how such a notice LOOKS. It knows nothing about why one is showing:
 * event-state policy, Oscar outcomes, export errors and authorization all stay
 * with their callers. Two of them were drawing this box by hand — the #481
 * event-state warning and the "Cannot safely cancel this game" blockers card —
 * with six tone strings duplicated between them, stacked one above the other in
 * the same panel. They matched until someone touched one.
 *
 * ── Passive by construction ─────────────────────────────────────────
 * There is no handler in the interface, no dismiss affordance, and no order of
 * any kind. It renders text and one decorative icon.
 *
 * `title` and `action` are `string` rather than `ReactNode` for that reason and
 * that reason only: a caller cannot hand this component a control it would
 * dutifully frame. `action` in particular is CONTENT — the manual out it points
 * at already exists on the host surface, and this only names it — so typing it
 * as text is what keeps it from quietly becoming a button.
 *
 * `explanation` IS `ReactNode`, and is the one deliberate opening. Blocker copy
 * arrives as several lines that need their own wrapping class (see below), and
 * no caller-agnostic string shape expresses that without this component taking
 * over per-line styling it has no business owning. Passing a control through it
 * would typecheck; nothing but review stops that.
 *
 * ── The live region is NOT here ─────────────────────────────────────
 * Whether a notice announces itself is policy, not presentation, so it belongs
 * to the caller. `EventStateWarning` wraps this in an always-mounted
 * `role="status"` because its warning can appear while the trader sits on the
 * ticket; the blockers card has never announced and does not start now. Putting
 * the region in here would hand every caller announcement semantics nobody
 * asked for, and would make the mounted-while-empty contract — the part that
 * actually makes announcements fire — depend on props lining up correctly.
 *
 * ── Tone ────────────────────────────────────────────────────────────
 * Follows the rule `UnknownStatusBadge` and `OrderbookFreshness` already set
 * here: colored treatment is reserved for states we can CONFIRM. Hence the
 * severity names — they are a claim about the world, not a choice of paint.
 *   • `informational` is neutral/muted — nothing is known to be wrong; we
 *     simply cannot see. Alarm-coloring an information gap burns the color out.
 *   • `defect` is amber — something IS confirmed wrong.
 * WHICH icon follows from the same fact, which is why it is derived here rather
 * than passed: a caller that could name a severity and a contradicting icon
 * would be able to say two things at once. WHETHER there is an icon is a
 * separate question, and `showIcon` answers it — see below.
 *
 * The action line is deliberately muted under BOTH severities. It describes
 * what the operator can still do, which does not get worse because the notice
 * above it did.
 *
 * ── `showIcon` exists to keep this refactor invisible ────────────────
 * The blockers card has never had an icon. #493 is a behavior-preserving
 * extraction under #326 ("children preserve user behavior unless explicitly
 * stated"), so giving that card a glyph on the way past would be this PR
 * changing something a user can see, for no reason a user asked for. It opts
 * out; the event-state warning, which shipped with icons in #481, does not.
 *
 * This is a preservation seam, NOT a styling knob — do not reach for it to
 * make a new notice look different. A ticket that is allowed to change
 * appearance should delete the opt-out and give every notice the icon, rather
 * than add a second caller that hides it.
 */

import { AlertTriangle, HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * `defect` = a confirmed fault. `informational` = a gap in what we can see.
 * Only the first earns color; see the Tone note above.
 */
export type PassiveWarningSeverity = 'informational' | 'defect';

export interface PassiveOperatorWarningProps {
  severity: PassiveWarningSeverity;
  /** Short lead-in, the thing scanned first. Text, never markup — see above. */
  title: string;
  /**
   * Why the notice is showing. A single string renders as one wrapped
   * paragraph; a list of nodes renders as evenly spaced lines. Callers own
   * per-line wrapping overrides — blocker copy embeds order IDs and needs
   * `break-all` where prose wants `break-words`.
   */
  explanation: ReactNode;
  /**
   * The manual out that remains available, as text. Optional because most
   * notices name it inline in the explanation instead. Text, never markup —
   * this line describes an action, it never becomes one.
   */
  action?: string;
  /**
   * Defaults to true. Set false ONLY to preserve a caller's pre-#493
   * appearance — see the `showIcon` note above before using it.
   */
  showIcon?: boolean;
  /** Spacing for the host surface only; tone is owned here. */
  className?: string;
  testId?: string;
}

export function PassiveOperatorWarning({
  severity,
  title,
  explanation,
  action,
  showIcon = true,
  className,
  testId,
}: PassiveOperatorWarningProps) {
  const confirmedDefect = severity === 'defect';
  const Icon = confirmedDefect ? AlertTriangle : HelpCircle;

  return (
    <div
      data-testid={testId}
      className={`rounded border px-2.5 py-1.5 ${
        confirmedDefect
          ? 'border-amber-500/50 bg-amber-500/5'
          : 'border-muted-foreground/30 bg-muted/30'
      } ${className ?? ''}`}
    >
      <div
        className={`flex items-center gap-1.5 text-[11px] font-semibold ${
          confirmedDefect ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'
        }`}
      >
        {/* shrink-0 on the icon and normal wrapping on the prose below: at
            1470px the ticket column is ~330px wide, so this copy wraps to
            several lines. It must wrap rather than squeeze the icon out or
            push the card's right edge. */}
        {showIcon && <Icon size={12} className="shrink-0" aria-hidden="true" />}
        <span>{title}</span>
      </div>
      {/* `space-y-0.5` rather than a margin per line, so a multi-line caller
          and a single-paragraph one get the same gap under the title without
          either having to know which it is. */}
      <div
        className={`mt-0.5 space-y-0.5 whitespace-normal break-words text-[10px] ${
          confirmedDefect ? 'text-amber-800 dark:text-amber-200/90' : 'text-muted-foreground/80'
        }`}
      >
        {explanation}
      </div>
      {action && <div className="text-muted-foreground/70 mt-1 text-[10px]">{action}</div>}
    </div>
  );
}
